import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessTime } from '../lib/businessTime';
import {
  Input,
  Button,
  Avatar,
  Text,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
  TabList,
  Tab,
} from '@fluentui/react-components';
import {
  Send24Regular,
  MoreHorizontal24Regular,
  Edit24Regular,
  Search24Regular,
  ChevronUp20Regular,
  Pin24Regular,
  Image24Regular,
  Document24Regular,
  PersonCircle24Regular,
  Info24Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  pageContainer: {
    display: 'flex',
    height: 'calc(100vh - 100px)',
    
    ...shorthands.padding('16px'),
    gap: '16px',
  },
  // LEFT SIDEBAR - Conversation List
  leftSidebar: {
    width: '320px',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    ...shorthands.overflow('hidden'),
  },
  leftSidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  leftSidebarTitle: {
    fontSize: '24px',
    fontWeight: '700',
  },
  leftSidebarActions: {
    display: 'flex',
    gap: '8px',
  },
  searchBox: {
    ...shorthands.padding('12px', '16px'),
  },
  filterTabs: {
    ...shorthands.padding('0', '16px'),
  },
  conversationList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...shorthands.padding('8px', '0'),
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralStroke1,
      ...shorthands.borderRadius('4px'),
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  conversationItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  conversationContent: {
    flexGrow: 1,
    minWidth: 0,
  },
  conversationName: {
    fontWeight: '600',
    fontSize: '15px',
    marginBottom: '4px',
  },
  conversationPreview: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  conversationTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  // MIDDLE - Main Chat Area
  mainChatArea: {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    ...shorthands.overflow('hidden'),
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  chatHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  chatHeaderInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  chatHeaderName: {
    fontSize: '16px',
    fontWeight: '600',
  },
  chatHeaderStatus: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  messagesArea: {
    flexGrow: 1,
    overflowY: 'auto',
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralStroke1,
      ...shorthands.borderRadius('4px'),
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  message: {
    display: 'flex',
    gap: '12px',
    maxWidth: '70%',
  },
  messageSent: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  messageReceived: {
    alignSelf: 'flex-start',
  },
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  messageBubble: {
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    wordBreak: 'break-word',
  },
  messageBubbleSent: {
    backgroundColor: '#0078d4',
    color: '#ffffff',
  },
  messageBubbleReceived: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
  },
  messageInfo: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  inputArea: {
    display: 'flex',
    gap: '8px',
    ...shorthands.padding('16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke1),
    alignItems: 'center',
  },
  inputField: {
    flexGrow: 1,
  },
  // RIGHT SIDEBAR - Chat Info
  rightSidebar: {
    width: '320px',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralStroke1,
      ...shorthands.borderRadius('4px'),
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  rightSidebarProfile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.padding('24px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  rightSidebarProfileName: {
    fontSize: '18px',
    fontWeight: '600',
    marginTop: '12px',
    marginBottom: '4px',
  },
  rightSidebarProfileStatus: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '16px',
  },
  rightSidebarActions: {
    display: 'flex',
    gap: '24px',
  },
  rightSidebarAction: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  rightSidebarActionLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  rightSidebarSection: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  rightSidebarSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rightSidebarSectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
  },
  rightSidebarSectionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rightSidebarSectionItemLabel: {
    fontSize: '14px',
  },
  rightSidebarSectionContent: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease-in-out',
  },
  chevronRotated: {
    transform: 'rotate(180deg)',
    transition: 'transform 0.3s ease-in-out',
  },
  chevronNormal: {
    transform: 'rotate(0deg)',
    transition: 'transform 0.3s ease-in-out',
  },
  // Compose Mode Styles
  composeContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  composeToField: {
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  composeToLabel: {
    fontSize: '14px',
    fontWeight: '600',
  },
  composeContactsSection: {
    ...shorthands.padding('16px'),
  },
  composeContactsTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    marginBottom: '12px',
  },
  composeContactsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  composeContactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('6px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  composeContactName: {
    fontSize: '15px',
  },
});

interface ConvSummary {
  conversationId: string;
  subject: string | null;
  customerName: string;
  customerId: string | null;
  unreadCount: number;
  messageCount: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
}

interface Msg {
  id: string;
  text: string;
  sender: { id: string | null; name: string; type: string };
  timestamp: string;
  read: boolean;
}

interface Customer {
  id: string;
  name: string;
}

const MSG_POLL_INTERVAL = 3000;
const CONV_POLL_INTERVAL = 8000;

export default function Messaging() {
  const styles = useStyles();
  const [conversations, setConversations] = useState<ConvSummary[]>([]);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [selectedConv, setSelectedConv] = useState<ConvSummary | null>(null);
  const [convLoading, setConvLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [isComposeMode, setIsComposeMode] = useState(false);
  const [composeCustomer, setComposeCustomer] = useState<Customer | null>(null);
  const [composeSubject, setComposeSubject] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [chatInfoExpanded, setChatInfoExpanded] = useState(true);
  const [mediaFilesExpanded, setMediaFilesExpanded] = useState(true);
  const [rightSidebarVisible, setRightSidebarVisible] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const msgIdsRef = useRef<Set<string>>(new Set());

  const adminHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadConversations = useCallback(async () => {
    try {
      const r = await apiFetch(`/staff/messages/conversations`, { headers: adminHeaders() });
      if (r.ok) {
        const data = await r.json();
        setConversations(data.conversations || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadConversations().finally(() => setConvLoading(false));
  }, [loadConversations]);

  useEffect(() => {
    const id = setInterval(loadConversations, CONV_POLL_INTERVAL);
    return () => clearInterval(id);
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedConv) return;
    setMsgLoading(true);
    setMessages([]);
    msgIdsRef.current = new Set();
    const convId = selectedConv.conversationId;
    apiFetch(`/conversations/${convId}/messages`, { headers: adminHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const msgs: Msg[] = data.messages || [];
        setMessages(msgs);
        msgIdsRef.current = new Set(msgs.map(m => m.id));
      })
      .catch(() => {})
      .finally(() => setMsgLoading(false));
  }, [selectedConv?.conversationId]);

  useEffect(() => {
    if (!selectedConv) return;
    const convId = selectedConv.conversationId;
    const id = setInterval(async () => {
      try {
        const r = await apiFetch(`/conversations/${convId}/messages`, { headers: adminHeaders() });
        if (!r.ok) return;
        const data = await r.json();
        const msgs: Msg[] = data.messages || [];
        const hasNew = msgs.some(m => !msgIdsRef.current.has(m.id));
        if (hasNew) {
          setMessages(msgs);
          msgIdsRef.current = new Set(msgs.map(m => m.id));
        }
      } catch {}
    }, MSG_POLL_INTERVAL);
    return () => clearInterval(id);
  }, [selectedConv?.conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isComposeMode || customers.length > 0) return;
    apiFetch(`/customers`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setCustomers(data.data || []); })
      .catch(() => {});
  }, [isComposeMode, customers.length]);

  const handleSend = async () => {
    if (!inputValue.trim() || !selectedConv || sending) return;
    const text = inputValue.trim();

    if (text === '/clear') {
      setInputValue('');
      await apiFetch(`/conversations/${selectedConv.conversationId}/messages`, {
        method: 'DELETE',
        headers: adminHeaders(),
      });
      setMessages([]);
      msgIdsRef.current.clear();
      return;
    }

    setSending(true);
    setInputValue('');
    try {
      const r = await apiFetch(`/conversations/${selectedConv.conversationId}/messages`, {
        method: 'POST',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.message) {
          setMessages(prev => {
            msgIdsRef.current.add(data.message.id);
            return [...prev, data.message];
          });
        }
      }
    } catch {
      setInputValue(text);
    } finally {
      setSending(false);
    }
  };

  const handleComposeSend = async () => {
    if (!composeCustomer || !inputValue.trim() || sending) return;
    setSending(true);
    const text = inputValue.trim();
    try {
      const r = await apiFetch(`/staff/messages/conversations`, {
        method: 'POST',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: composeCustomer.id,
          subject: composeSubject.trim() || undefined,
          message: text,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const newConv: ConvSummary = data.conversation;
        setConversations(prev => [newConv, ...prev]);
        setSelectedConv(newConv);
        setMessages(data.message ? [data.message] : []);
        msgIdsRef.current = new Set(data.message ? [data.message.id] : []);
        setIsComposeMode(false);
        setComposeCustomer(null);
        setComposeSubject('');
        setCustomerSearch('');
        setInputValue('');
        setRightSidebarVisible(true);
      }
    } catch {
    } finally {
      setSending(false);
    }
  };

  const formatRelTime = (ts: string | null) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 60) return mins <= 1 ? 'now' : `${mins}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return `${Math.floor(days / 7)}w`;
  };

  const formatMsgTime = (ts: string) => formatBusinessTime(ts);

  const filteredConversations = conversations.filter(c => {
    if (selectedFilter === 'unread' && c.unreadCount === 0) return false;
    if (searchText && !c.customerName?.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  const filteredCustomers = customers
    .filter(c => !customerSearch || c.name?.toLowerCase().includes(customerSearch.toLowerCase()))
    .slice(0, 50);

  return (
    <div className={styles.pageContainer}>
      {/* LEFT SIDEBAR - Conversation List */}
      <div className={styles.leftSidebar}>
        <div className={styles.leftSidebarHeader}>
          <Text className={styles.leftSidebarTitle}>Messages</Text>
          <div className={styles.leftSidebarActions}>
            <Button
              appearance="subtle"
              icon={<MoreHorizontal24Regular />}
              aria-label="More options"
            />
            <Button
              appearance="subtle"
              icon={<Edit24Regular />}
              aria-label="New message"
              onClick={() => {
                setIsComposeMode(true);
                setComposeCustomer(null);
                setComposeSubject('');
                setCustomerSearch('');
                setInputValue('');
                setRightSidebarVisible(false);
              }}
            />
          </div>
        </div>

        <div className={styles.searchBox}>
          <Input
            contentBefore={<Search24Regular />}
            placeholder="Search Messages"
            style={{ width: '100%' }}
            value={searchText}
            onChange={(_, d) => setSearchText(d.value)}
          />
        </div>

        <div className={styles.filterTabs}>
          <TabList
            selectedValue={selectedFilter}
            onTabSelect={(_, data) => setSelectedFilter(data.value as string)}
          >
            <Tab value="all">All</Tab>
            <Tab value="unread">Unread</Tab>
          </TabList>
        </div>

        <div className={styles.conversationList}>
          {convLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
              <Spinner size="small" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div style={{ padding: '24px 16px', color: tokens.colorNeutralForeground3, fontSize: '13px', textAlign: 'center' }}>
              No conversations yet
            </div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv.conversationId}
                className={`${styles.conversationItem} ${
                  selectedConv?.conversationId === conv.conversationId && !isComposeMode
                    ? styles.conversationItemActive
                    : ''
                }`}
                onClick={() => {
                  setSelectedConv(conv);
                  setIsComposeMode(false);
                  setRightSidebarVisible(true);
                }}
              >
                <Avatar name={conv.customerName} color="colorful" size={56} />
                <div className={styles.conversationContent}>
                  <div className={styles.conversationName}>{conv.customerName}</div>
                  {conv.lastMessage && (
                    <div className={styles.conversationPreview}>{conv.lastMessage}</div>
                  )}
                  {conv.unreadCount > 0 && (
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: tokens.colorBrandBackground,
                      color: 'white',
                      borderRadius: '10px',
                      padding: '1px 7px',
                      fontSize: '11px',
                      fontWeight: '600',
                      marginTop: '2px',
                    }}>
                      {conv.unreadCount}
                    </div>
                  )}
                </div>
                <div className={styles.conversationTime}>{formatRelTime(conv.lastMessageAt)}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* MIDDLE - Main Chat Area */}
      <div className={styles.mainChatArea}>
        {isComposeMode ? (
          <div className={styles.composeContainer}>
            {/* To: field */}
            <div className={styles.composeToField}>
              <Text className={styles.composeToLabel}>To:</Text>
              {composeCustomer ? (
                <>
                  <Avatar name={composeCustomer.name} size={20} color="colorful" />
                  <Text style={{ fontWeight: '500' }}>{composeCustomer.name}</Text>
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => setComposeCustomer(null)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Change
                  </Button>
                </>
              ) : (
                <Input
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(_, d) => setCustomerSearch(d.value)}
                  style={{ flexGrow: 1, border: 'none' }}
                  autoFocus
                />
              )}
            </div>

            {composeCustomer ? (
              <>
                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                  <Input
                    placeholder="Subject (optional)"
                    value={composeSubject}
                    onChange={(_, d) => setComposeSubject(d.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ flexGrow: 1 }} />
                <div className={styles.inputArea}>
                  <Input
                    className={styles.inputField}
                    placeholder="Type your message..."
                    value={inputValue}
                    onChange={(_, d) => setInputValue(d.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComposeSend(); } }}
                  />
                  <Button
                    appearance="transparent"
                    icon={<Send24Regular />}
                    onClick={handleComposeSend}
                    disabled={!inputValue.trim() || sending}
                  />
                </div>
              </>
            ) : (
              <div className={styles.composeContactsSection}>
                <Text className={styles.composeContactsTitle}>
                  {customerSearch ? `Results for "${customerSearch}"` : 'Your customers'}
                </Text>
                <div className={styles.composeContactsList}>
                  {customers.length === 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" />
                    </div>
                  ) : filteredCustomers.length === 0 ? (
                    <Text style={{ color: tokens.colorNeutralForeground3, fontSize: '13px' }}>
                      No customers found
                    </Text>
                  ) : (
                    filteredCustomers.map(c => (
                      <div
                        key={c.id}
                        className={styles.composeContactItem}
                        onClick={() => setComposeCustomer(c)}
                      >
                        <Avatar name={c.name} color="colorful" size={40} />
                        <Text className={styles.composeContactName}>{c.name}</Text>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ) : selectedConv ? (
          <>
            <div className={styles.chatHeader}>
              <div className={styles.chatHeaderLeft}>
                <Avatar name={selectedConv.customerName} color="colorful" size={40} />
                <div className={styles.chatHeaderInfo}>
                  <Text className={styles.chatHeaderName}>{selectedConv.customerName}</Text>
                  {selectedConv.subject && (
                    <Text className={styles.chatHeaderStatus}>{selectedConv.subject}</Text>
                  )}
                </div>
              </div>
              <Button
                appearance="subtle"
                icon={<Info24Regular />}
                aria-label="Toggle chat info"
                onClick={() => setRightSidebarVisible(!rightSidebarVisible)}
              />
            </div>

            <div className={styles.messagesArea}>
              {msgLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <Spinner size="small" />
                </div>
              ) : messages.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: tokens.colorNeutralForeground3, fontSize: '13px' }}>
                  No messages yet
                </div>
              ) : (
                messages.map(msg => {
                  const isSent = msg.sender?.type === 'staff';
                  return (
                    <div
                      key={msg.id}
                      className={`${styles.message} ${isSent ? styles.messageSent : styles.messageReceived}`}
                    >
                      <Avatar name={msg.sender?.name || '?'} color="colorful" size={32} />
                      <div className={styles.messageContent}>
                        <div className={`${styles.messageBubble} ${isSent ? styles.messageBubbleSent : styles.messageBubbleReceived}`}>
                          <Text>{msg.text}</Text>
                        </div>
                        <Text className={styles.messageInfo}>
                          {msg.sender?.name} • {msg.timestamp ? formatMsgTime(msg.timestamp) : ''}
                        </Text>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className={styles.inputArea}>
              <Input
                className={styles.inputField}
                placeholder="Aa"
                value={inputValue}
                onChange={(_, d) => setInputValue(d.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              />
              <Button
                appearance="transparent"
                icon={<Send24Regular />}
                onClick={handleSend}
                disabled={!inputValue.trim() || sending}
              />
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: tokens.colorNeutralForeground3, gap: '12px' }}>
            <Edit24Regular style={{ fontSize: '48px', opacity: 0.3 }} />
            <Text style={{ fontSize: '15px' }}>Select a conversation or start a new one</Text>
          </div>
        )}
      </div>

      {/* RIGHT SIDEBAR - Chat Info */}
      {rightSidebarVisible && selectedConv && !isComposeMode && (
        <div className={styles.rightSidebar}>
          <div className={styles.rightSidebarProfile}>
            <Avatar name={selectedConv.customerName} color="colorful" size={96} />
            <Text className={styles.rightSidebarProfileName}>{selectedConv.customerName}</Text>
            {selectedConv.subject && (
              <Text className={styles.rightSidebarProfileStatus}>{selectedConv.subject}</Text>
            )}
            <div className={styles.rightSidebarActions}>
              <div className={styles.rightSidebarAction}>
                <Button appearance="transparent" icon={<PersonCircle24Regular />} size="large" />
                <Text className={styles.rightSidebarActionLabel}>Profile</Text>
              </div>
              <div className={styles.rightSidebarAction}>
                <Button appearance="transparent" icon={<Search24Regular />} size="large" />
                <Text className={styles.rightSidebarActionLabel}>Search</Text>
              </div>
            </div>
          </div>

          <div className={styles.rightSidebarSection}>
            <div
              className={styles.rightSidebarSectionHeader}
              onClick={() => setChatInfoExpanded(!chatInfoExpanded)}
            >
              <Text className={styles.rightSidebarSectionTitle}>Chat info</Text>
              <div className={chatInfoExpanded ? styles.chevronNormal : styles.chevronRotated}>
                <ChevronUp20Regular />
              </div>
            </div>
            {chatInfoExpanded && (
              <div className={styles.rightSidebarSectionContent}>
                <div className={styles.rightSidebarSectionItem}>
                  <Pin24Regular />
                  <Text className={styles.rightSidebarSectionItemLabel}>View pinned messages</Text>
                </div>
              </div>
            )}
          </div>

          <div className={styles.rightSidebarSection}>
            <div
              className={styles.rightSidebarSectionHeader}
              onClick={() => setMediaFilesExpanded(!mediaFilesExpanded)}
            >
              <Text className={styles.rightSidebarSectionTitle}>Media & files</Text>
              <div className={mediaFilesExpanded ? styles.chevronNormal : styles.chevronRotated}>
                <ChevronUp20Regular />
              </div>
            </div>
            {mediaFilesExpanded && (
              <div className={styles.rightSidebarSectionContent}>
                <div className={styles.rightSidebarSectionItem}>
                  <Image24Regular />
                  <Text className={styles.rightSidebarSectionItemLabel}>Media</Text>
                </div>
                <div className={styles.rightSidebarSectionItem}>
                  <Document24Regular />
                  <Text className={styles.rightSidebarSectionItemLabel}>Files</Text>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}